/**
 * Editor State Machine Hook
 * Manages editor lifecycle with explicit state transitions
 */

import { useReducer, useCallback, useMemo } from 'react';
import type {
  EditorState,
  EditorAction,
  EditorStateContext,
  EditorConfig,
  EditorError,
} from '../types/editor.types';
import { canTransition } from '../types/editor.types';

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialContext: EditorStateContext = {
  state: 'IDLE',
  config: null,
  error: null,
  isDocumentLoaded: false,
  hasUnsavedChanges: false,
};

// =============================================================================
// REDUCER
// =============================================================================

function editorReducer(
  context: EditorStateContext,
  action: EditorAction
): EditorStateContext {
  const { state } = context;

  switch (action.type) {
    case 'EDITOR_CREATED': {
      // EDITOR_CREATED is idempotent. If we're already in an operational state (config received + applied),
      // this is a no-op. Otherwise, transition to waiting/configuring.
      if (state === 'IDLE' && !context.config) {
        // Editor created before config arrived — wait for config
        if (!canTransition(state, 'WAITING_FOR_CONFIG')) {
          console.warn(`[StateMachine] Invalid transition: ${state} → WAITING_FOR_CONFIG`);
          return context;
        }
        console.log('[StateMachine] Editor created, waiting for config');
        return {
          ...context,
          state: 'WAITING_FOR_CONFIG',
          error: null,
        };
      }
      
      if (state === 'IDLE' && context.config) {
        // Config arrived before editor created — transition to CONFIGURING
        if (!canTransition(state, 'CONFIGURING')) {
          console.warn(`[StateMachine] Invalid transition: ${state} → CONFIGURING (with pre-received config)`);
          return context;
        }
        console.log('[StateMachine] Editor created with pre-received config, going to CONFIGURING');
        return {
          ...context,
          state: 'CONFIGURING',
          error: null,
        };
      }
      
      // Already in an operational state (CONFIGURING, READY, etc.) — this is a late/redundant created event, ignore it
      console.log(`[StateMachine] Editor created event ignored (state already ${state})`);
      return context;
    }

    case 'CONFIG_RECEIVED': {
      console.log(state)
      // Can receive config in IDLE (before editor created), WAITING_FOR_CONFIG,
      // or any operational/error state (reinitialize on navigation).
      // If in IDLE, just store the config — we'll transition to CONFIGURING when editor is created.
      // Otherwise, transition to CONFIGURING immediately.
      if (state === 'IDLE') {
        return {
          ...context,
          state: 'IDLE',
          config: action.payload,
          isDocumentLoaded: false,
          hasUnsavedChanges: false,
          error: null,
        };
      }

      if (!canTransition(state, 'CONFIGURING')) {
        console.warn(`[StateMachine] Invalid transition: ${state} → CONFIGURING (config received)`);
        return context;
      }
      
      console.log(`[StateMachine] Config received in ${state}, transitioning to CONFIGURING`);
      
      return {
        ...context,
        state: 'CONFIGURING',
        config: action.payload,
        isDocumentLoaded: false,
        hasUnsavedChanges: false,
        error: null,
      };
    }

    case 'CONFIG_APPLIED': {
      if (!canTransition(state, 'READY')) {
        console.warn(`[StateMachine] Invalid transition: ${state} → READY`);
        return context;
      }
      return {
        ...context,
        state: 'READY',
        error: null,
      };
    }

    case 'LOAD_DOCUMENT': {
      if (!canTransition(state, 'LOADING_DOCUMENT')) {
        console.warn(`[StateMachine] Invalid transition: ${state} → LOADING_DOCUMENT`);
        return context;
      }
      return {
        ...context,
        state: 'LOADING_DOCUMENT',
        error: null,
      };
    }

    case 'DOCUMENT_LOADED': {
      console.log(context)
      // Branch based on latest reducer context, not callback closure state.
      // Control copies (editable) must enter APPLYING_PROTECTION before ACTIVE.
      const nextState: EditorState =
        context.config?.documentType === 'control' && context.config?.canEdit
          ? 'APPLYING_PROTECTION'
          : 'ACTIVE';
      console.log(`[StateMachine] Document loaded, transitioning to ${nextState}`);
      if (!canTransition(state, nextState)) {
        console.warn(`[StateMachine] Invalid transition: ${state} → ${nextState}`);
        return context;
      }
      return {
        ...context,
        state: nextState,
        isDocumentLoaded: true,
        hasUnsavedChanges: false,
        error: null,
      };
    }

    case 'APPLY_PROTECTION': {
      console.log(state)
      if (!canTransition(state, 'APPLYING_PROTECTION')) {
        console.warn(`[StateMachine] Invalid transition: ${state} → APPLYING_PROTECTION`);
        return context;
      }
      return {
        ...context,
        state: 'APPLYING_PROTECTION',
        isDocumentLoaded: true,
        hasUnsavedChanges: false,
        error: null,
      };
    }

    case 'PROTECTION_APPLIED': {
      if (!canTransition(state, 'READY')) {
        console.warn(`[StateMachine] Invalid transition: ${state} → READY`);
        return context;
      }
      return {
        ...context,
        state: 'READY',
        hasUnsavedChanges: false,
        error: null,
      };
    }

    case 'ACTIVATE_EDITOR': {
      if (!canTransition(state, 'ACTIVE')) {
        console.warn(`[StateMachine] Invalid transition: ${state} → ACTIVE`);
        return context;
      }
      return {
        ...context,
        state: 'ACTIVE',
        error: null,
      };
    }

    case 'REINITIALIZE': {
      // Special action: Can happen from ACTIVE state to reconfigure (Save As scenario)
      if (!canTransition(state, 'CONFIGURING')) {
        console.warn(`[StateMachine] Invalid transition: ${state} → CONFIGURING (reinitialize)`);
        return context;
      }
      return {
        ...context,
        state: 'CONFIGURING',
        config: action.payload,
        isDocumentLoaded: false,
        hasUnsavedChanges: false,
        error: null,
      };
    }

    case 'ERROR': {
      return {
        ...context,
        state: 'ERROR',
        error: action.payload,
      };
    }

    case 'RESET': {
      return initialContext;
    }

    default: {
      console.warn(`[StateMachine] Unknown action: ${(action as { type: string }).type}`);
      return context;
    }
  }
}

// =============================================================================
// HOOK
// =============================================================================

export interface UseEditorStateMachineReturn {
  /** Current state context */
  context: EditorStateContext;
  
  /** Current state shorthand */
  state: EditorState;
  
  /** Current config shorthand */
  config: EditorConfig | null;
  
  /** State checks */
  isIdle: boolean;
  isWaitingForConfig: boolean;
  isConfiguring: boolean;
  isReady: boolean;
  isLoadingDocument: boolean;
  isApplyingProtection: boolean;
  isActive: boolean;
  isError: boolean;
  
  /** Compound state checks */
  isOperational: boolean; // READY, LOADING_DOCUMENT, APPLYING_PROTECTION, or ACTIVE
  canAcceptConfig: boolean; // WAITING_FOR_CONFIG or ACTIVE (for reinit)
  canLoadDocument: boolean; // READY
  
  /** Actions */
  onEditorCreated: () => void;
  onConfigReceived: (config: EditorConfig) => void;
  onConfigApplied: () => void;
  onLoadDocument: () => void;
  onDocumentLoaded: () => void;
  onApplyProtection: () => void;
  onProtectionApplied: () => void;
  onActivateEditor: () => void;
  onReinitialize: (config: EditorConfig) => void;
  onError: (error: EditorError) => void;
  onReset: () => void;
  
  /** Content change tracking */
  setHasUnsavedChanges: (value: boolean) => void;
}

export function useEditorStateMachine(): UseEditorStateMachineReturn {
  const [context, dispatch] = useReducer(editorReducer, initialContext);

  // State checks
  const stateChecks = useMemo(() => ({
    isIdle: context.state === 'IDLE',
    isWaitingForConfig: context.state === 'WAITING_FOR_CONFIG',
    isConfiguring: context.state === 'CONFIGURING',
    isReady: context.state === 'READY',
    isLoadingDocument: context.state === 'LOADING_DOCUMENT',
    isApplyingProtection: context.state === 'APPLYING_PROTECTION',
    isActive: context.state === 'ACTIVE',
    isError: context.state === 'ERROR',
    isOperational: ['READY', 'LOADING_DOCUMENT', 'APPLYING_PROTECTION', 'ACTIVE'].includes(context.state),
    canAcceptConfig: ['IDLE', 'WAITING_FOR_CONFIG', 'ACTIVE'].includes(context.state),
    canLoadDocument: context.state === 'READY',
  }), [context.state]);

  // Actions
  const onEditorCreated = useCallback(() => {
    console.log('[StateMachine] Editor created');
    dispatch({ type: 'EDITOR_CREATED' });
  }, []);

  const onConfigReceived = useCallback((config: EditorConfig) => {
    console.log('[StateMachine] Config received', config);
    dispatch({ type: 'CONFIG_RECEIVED', payload: config });
  }, []);

  const onConfigApplied = useCallback(() => {
    console.log('[StateMachine] Config applied');
    dispatch({ type: 'CONFIG_APPLIED' });
  }, []);

  const onLoadDocument = useCallback(() => {
    console.log('[StateMachine] Loading document');
    dispatch({ type: 'LOAD_DOCUMENT' });
  }, []);

  const onDocumentLoaded = useCallback(() => {
    console.log('[StateMachine] Document loaded');
    dispatch({ type: 'DOCUMENT_LOADED' });
  }, []);

  const onApplyProtection = useCallback(() => {
    console.log('[StateMachine] Applying protection');
    dispatch({ type: 'APPLY_PROTECTION' });
  }, []);

  const onProtectionApplied = useCallback(() => {
    console.log('[StateMachine] Protection applied');
    dispatch({ type: 'PROTECTION_APPLIED' });
  }, []);

  const onActivateEditor = useCallback(() => {
    console.log('[StateMachine] Activating editor');
    dispatch({ type: 'ACTIVATE_EDITOR' });
  }, []);

  const onReinitialize = useCallback((config: EditorConfig) => {
    console.log('[StateMachine] Reinitializing with new config', config);
    dispatch({ type: 'REINITIALIZE', payload: config });
  }, []);

  const onError = useCallback((error: EditorError) => {
    console.error('[StateMachine] Error', error);
    dispatch({ type: 'ERROR', payload: error });
  }, []);

  const onReset = useCallback(() => {
    console.log('[StateMachine] Reset');
    dispatch({ type: 'RESET' });
  }, []);

  // Content change tracking (doesn't affect state machine, just context)
  const setHasUnsavedChanges = useCallback((_value: boolean) => {
    // This is a special case - we update context without state transition
    // In a real implementation, you might want a separate useState for this
    // For now, we'll handle it through the reducer
  }, []);

  return {
    context,
    state: context.state,
    config: context.config,
    ...stateChecks,
    onEditorCreated,
    onConfigReceived,
    onConfigApplied,
    onLoadDocument,
    onDocumentLoaded,
    onApplyProtection,
    onProtectionApplied,
    onActivateEditor,
    onReinitialize,
    onError,
    onReset,
    setHasUnsavedChanges,
  };
}